import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDownRight, MapPin } from "lucide-react";
import { useLang, CONTACT } from "../../translations";
import { LineReveal } from "./Reveal";

const PORTRAIT = "https://images.unsplash.com/photo-1774348599598-5bd3c6c88ef4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} id="hero" data-testid="hero-section" className="relative min-h-[100svh] flex items-end overflow-hidden pt-28 pb-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12 w-full grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7">
          <motion.p
            data-testid="hero-kicker"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
            className="text-xs md:text-sm uppercase tracking-[0.28em] text-sage mb-6 flex items-center gap-2"
          >
            <span className="h-px w-8 bg-sage inline-block" /> {t.hero.kicker}
          </motion.p>

          <h1 data-testid="hero-title" className="font-heading text-ink text-5xl sm:text-6xl md:text-7xl lg:text-[5.4rem] leading-[0.98] tracking-tight">
            <LineReveal lines={t.hero.title} delay={0.35} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }}
            className="mt-8 max-w-xl text-lg md:text-xl text-muted2 leading-relaxed"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid="hero-book-btn"
              onClick={() => go("contact")}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-ink text-cream px-8 py-4 text-base hover:bg-sage transition-colors duration-300"
            >
              {t.hero.ctaBook}
              <ArrowDownRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
            </button>
            <a
              data-testid="hero-call-btn"
              href={`tel:${CONTACT.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-ink/20 text-ink px-8 py-4 text-base hover:border-sage hover:text-sage transition-colors duration-300"
            >
              <Phone size={17} /> {t.hero.ctaCall} · {CONTACT.phone}
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 1 }}
          className="lg:col-span-5 relative"
        >
          <div className="img-zoom-wrap relative rounded-[2rem] overflow-hidden aspect-[4/5] shadow-[0_30px_80px_-20px_rgba(28,37,34,0.35)]">
            <motion.img style={{ y, scale }} src={PORTRAIT} alt={CONTACT.name} className="h-full w-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-ink/50 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-cream">
              <div>
                <p className="font-heading text-2xl leading-none">{CONTACT.name}</p>
                <p className="text-[11px] uppercase tracking-[0.2em] mt-1 opacity-90">{t.hero.credential}</p>
              </div>
            </div>
          </div>
          <div className="absolute -top-4 -left-4 hidden md:flex items-center gap-2 rounded-full bg-cream border border-ink/10 px-4 py-2 shadow-lg">
            <MapPin size={15} className="text-sage" />
            <span className="text-xs text-ink">La Tour-de-Peilz · Riviera</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
